import { FC, useEffect, useMemo } from 'react'
import { useGalleryAssignment, useGalleryProduct } from '../../contexts'

import { ClientGalleryCreateRequestDrawer } from 'components/pages/Client/ClientGallery/ClientGalleryCreateRequestDrawer'
import { ClientGalleryDocumentTimeline } from '../ClientGalleryDocumentTimeline'
import { ClientGalleryDocumentTypeFilter } from '../ClientGalleryOrderTypeFilter'
import { ClientGalleryRequestThreadList } from '../ClientGalleryRequestThreadList'
import { RequestThread } from 'components/common/Threads'
import { Stack } from '@mui/material'
import { useClientGallery } from '../_main/contexts'
import { useThreads } from 'components/common/Threads/context'
import { useTranslation } from 'react-i18next'

/**
 * Client gallery requests content to display the threads for each assignment.
 *
 * @example <ClientGalleryRequests />
 */
export const ClientGalleryRequests: FC = () => {

  const { t } = useTranslation(['threads'])

  const { isEnergyCertificateProduct, isAuthoritiesDocumentsProduct } = useGalleryProduct()
  const { allowClientToCreateThreads } = useGalleryAssignment()

  const {
    setHeaderButtonText,
    setAllowUploadOfDocuments,
    assignmentThreadsData,
    threadList,
    fileAPIOptions,
    allowCreateRequestEnergyCertificate,
    handleOnCreateRequestThread,
    handleCloseCreateRequestDrawer,
    isCreateRequestDrawerOpen,
    selectedThread,
    setSelectedThread
  } = useThreads()

  const {
    assignmentId,
    assignmentDocumentsStatusHistoryData,
    assignmentDocumentsEstimatedDelivery,
    showDocumentStatusHistory,
    isPropertyPathname
  } = useClientGallery()

  const showDocAndThreadArea = useMemo(() => {
    return showDocumentStatusHistory || (assignmentThreadsData && assignmentThreadsData?.length > 0)
  }, [showDocumentStatusHistory, assignmentThreadsData])

  useEffect(() => {
    // Hide the create thread button if in property pathname.
    if (isPropertyPathname) return

    // Energy certificate product validations to allow the client to create threads.
    if (allowCreateRequestEnergyCertificate && allowClientToCreateThreads) {
      setHeaderButtonText(t('create_thread.title'))
    }

    // Authority documents product validations to allow the client to create threads.
    if (isAuthoritiesDocumentsProduct && allowClientToCreateThreads) {
      setHeaderButtonText(t('create_thread.title'))
    }

    if (isEnergyCertificateProduct) {
      setAllowUploadOfDocuments(false)
    }
    // No need to add isEnergyCertificateProduct to the dependencies array as it's included in allowCreateRequestEnergyCertificate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowCreateRequestEnergyCertificate])

  return (
    <Stack width="100%">

      <ClientGalleryDocumentTypeFilter />

      {/** Content changes according to the selected assignment from the tabs. */}
      <Stack width={{ md: '100%', lg: '80%' }} marginTop="3rem" flexDirection="row" gap="2.4rem" alignItems="flex-start">

        {showDocAndThreadArea &&
          <Stack gap="1.6rem">

            {threadList.length > 0 &&
              <ClientGalleryRequestThreadList
                threadMessageItems={threadList}
                selectedThread={selectedThread}
                setSelectedThread={setSelectedThread}
              />
            }

            {showDocumentStatusHistory &&
              <ClientGalleryDocumentTimeline
                documentStatusHistoryData={assignmentDocumentsStatusHistoryData}
                assignmentDocumentsEstimatedDelivery={assignmentDocumentsEstimatedDelivery}
              />
            }

          </Stack>
        }

        <RequestThread
          assignmentId={assignmentId}
          title={t('request_supplier_title')}
          threadMessageItems={selectedThread ? [selectedThread] : []}
          fileUploadOptions={fileAPIOptions}
          onHeaderButtonClick={handleOnCreateRequestThread}
        />

      </Stack>

      <ClientGalleryCreateRequestDrawer
        isOpen={isCreateRequestDrawerOpen}
        onClose={handleCloseCreateRequestDrawer}
        allowUploadOfDocuments={!isEnergyCertificateProduct}
      />

    </Stack>
  )
}
